import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useSession } from '@battleops/client/src/context/SessionContext';
import { platform } from '@battleops/client/src/platform';
import Rail from './Rail';
import TopBar from './TopBar';
import DetailPanel from './DetailPanel';
import CommandBar from './CommandBar';
import Ringer from '../components/Ringer';
import RoutineMode from '../components/RoutineMode';
import MetricCapture from '../components/MetricCapture';
import DoneNote from '../components/automation/DoneNote';
import Toasts from '../components/Toasts';
import { useBudgetNotices } from '@battleops/client/src/data/useProfit';
import { DialogHost } from '../components/Dialog';
import TaskModal from '../components/TaskModal';
import { ToolSlot, useToolSlot } from './ToolDock';
import { usePanel, usePref, useKey, useTaskModal } from '../hooks';
import s from './shell.module.css';

const PEEK_IN_MS = 120;    // hover this long before the unlocked drawer slides out (a pass-by doesn't open it)
const PEEK_OUT_MS = 240;

// One scaffold: a frame (app bar across the top, the drawer down the left, same dark chrome) wrapping the page, which
// sits inset with rounded corners; side panels dock into the frame beside it as their own rounded sheets. The drawer is
// locked open, or unlocked — a slim strip that slides open inside the frame (the page makes room) while the cursor is
// on it. ‹ or Esc closes it, and it stays closed until the cursor has left.
export default function Shell() {
  const { syncNow } = useSession();
  const [locked, setLocked] = usePref('rail.locked', true);
  const [peek, setPeek] = useState(false);
  const [cmd, setCmd] = useState(null);           // null | '' | 'add' — the command bar and its starting mode
  const panel = usePanel();
  const tools = useToolSlot();
  const details = useTaskModal();
  const timer = useRef(null);
  const hold = useRef(false);     // closed by hand: don't reopen until the cursor leaves the drawer

  // Back in view (another window, the phone just clocked in): sync now instead of waiting for the minute tick.
  useEffect(() => platform().appState.subscribe((st) => { if (st === 'active') syncNow(); }), [syncNow]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const toggleLock = useCallback(() => { setLocked((l) => { if (l) hold.current = true; return !l; }); setPeek(false); }, [setLocked]);
  const closeDrawer = useCallback(() => { clearTimeout(timer.current); hold.current = true; setPeek(false); }, []);
  // Search: / from anywhere you're not typing; Ctrl Shift / from inside a note or a field (a plain / types there).
  // Ctrl K stays for the keyboard crowd.
  const openSearch = useCallback(() => setCmd((c) => (c === null ? '' : c)), []);
  useKey('/', openSearch);
  useKey('mod+shift+/', openSearch, { inFields: true });
  useKey('mod+k', useCallback(() => setCmd((c) => (c === null ? '' : null)), []), { inFields: true });
  useKey('[', toggleLock);
  useKey('n', useCallback(() => setCmd('add'), []));
  useKey('escape', useCallback(() => {
    if (cmd !== null) setCmd(null); else if (peek) closeDrawer(); else if (panel.kind) panel.close();
  }, [cmd, peek, panel, closeDrawer]), { inFields: true });

  const hover = (on) => {
    if (locked) return;
    if (!on) hold.current = false;
    else if (hold.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setPeek(on), on ? PEEK_IN_MS : PEEK_OUT_MS);
  };
  const open = locked || peek;

  return (
    <ToolSlot.Provider value={tools.value}>
    <div className={s.shell} data-unlocked={!locked || undefined} data-peek={(!locked && peek) || undefined} data-panel={panel.kind ? '' : undefined} data-tools={tools.docked || undefined}>
      <TopBar onCommand={() => setCmd('')} onAdd={() => setCmd('add')} />
      <div className={s.railSlot} onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)}
        onFocus={() => { if (!locked && !hold.current) setPeek(true); }}
        onBlur={(e) => { if (!locked && !e.currentTarget.contains(e.relatedTarget)) setPeek(false); }}>
        <Rail open={open} locked={locked} onLock={toggleLock} onClose={closeDrawer}
          onNavigate={() => { if (!locked) closeDrawer(); }} />
      </div>
      <main className={s.main}><Outlet /></main>
      {panel.kind ? <DetailPanel kind={panel.kind} id={panel.id} onClose={panel.close} /> : null}
      <div className={s.toolSlot} ref={tools.ref} />
      {cmd !== null ? <CommandBar mode={cmd} onClose={() => setCmd(null)} /> : null}
      {details.id ? <TaskModal id={details.id} onClose={details.close} /> : null}
      <Ringer />
      <RoutineMode />
      <MetricCapture />
      <DoneNote />
      <Toasts />
      <BudgetNotices />
      <DialogHost />
    </div>
    </ToolSlot.Provider>
  );
}

/** Owners: a budget passing 80 % / 100 % shows a notice once (CFO 5.2). */
/** Several at once show as one notice that opens the CFO budgets. */
function BudgetNotices() {
  const navigate = useNavigate();
  useBudgetNotices({ openBudgets: () => navigate('/cfo#cfo-budgets') });
  return null;
}
